import React from 'react';
import { Col, Form, FormInstance, Row } from 'antd';
import dayjs from 'dayjs';

import { clearFieldErrors, handleFormErrors } from '../../common/errorHelpers';
import { FormDatePicker } from '../../components/forms/FormDatePicker';
import { FormSelectOrAddItem } from '../../components/forms/FormSelectOrAddItem';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { CandidateAttachements } from '../candidate-details/CandidateAttachements';
import { useUpdateCandidate } from '../candidate-details/useUpdateCandidate';
import { CandidateType } from '../candidates/Candidates.types';
import { SaveButtonIcon } from '../dictionaries/DictionaryItemsList';
import { useGetDictionaryByName } from '../dictionaries/useGetDictionaryByName';
import { AttachmentType } from '../emails/Emails.type';

import { SubmitButton } from './RecruitmentDataForm';

export const descriptionLabelStyle = {
  fontSize: '14px',
  color: '#9e9e9e',
  paddingLeft: '5px',
  marginBottom: '-10px',
};

export const descriptionContentStyle = {
  backgroundColor: 'white',
  borderColor: 'rgb(217, 217, 217)',
  padding: '8px 11px',
  height: '38px',
};

type HRReviewProps = {
  candidate?: CandidateType;
  form?: FormInstance;
  standAloneMode?: boolean;
  hrInterviewAttachements: AttachmentType[];
  attachFile: (fileId: string) => void;
};

export const HRReview: React.FC<HRReviewProps> = ({
  candidate,
  form: candidateForm,
  standAloneMode,
  hrInterviewAttachements,
  attachFile,
}) => {
  const [newForm] = Form.useForm();
  const updateCandidate = useUpdateCandidate();

  const form = candidateForm ?? newForm;

  const { data: interviewAssessment, isLoading: interviewAssessmentLoading } =
    useGetDictionaryByName('interview-assessment');

  const interviewAssessmentOptions =
    interviewAssessment && !interviewAssessmentLoading
      ? interviewAssessment.map((assessment) => ({ value: assessment.value, content: assessment.value }))
      : [];

  const initialValues =
    standAloneMode && candidate
      ? {
          ...(candidate.hrInterviewDate ? { hrInterviewDate: dayjs.unix(candidate.hrInterviewDate) } : []),
          hrInterviewAssessment: candidate.hrInterviewAssessment,
          hrInterviewSummary: candidate.hrInterviewSummary,
        }
      : {};

  const submitForm = () => {
    if (!candidate) {
      return;
    }
    const dataValues = form.getFieldsValue();
    updateCandidate.mutate(
      {
        queryParams: { id: candidate.uuid },
        data: {
          ...dataValues,
          hrInterviewDate: dayjs(dataValues['hrInterviewDate']).unix(),
        },
      },
      {
        onSuccess: () => clearFieldErrors(form),
        onError: (error) => {
          handleFormErrors({ error, form });
        },
      }
    );
  };

  return (
    <Form form={form} initialValues={initialValues} layout="vertical" colon={false}>
      <StyledColumn gap="0.3rem">
        <Row gutter={[10, 5]}>
          <Col xs={24} sm={8}>
            <FormDatePicker name="hrInterviewDate" label="HR interview date" />
          </Col>
          <Col xs={24} sm={16}>
            <FormSelectOrAddItem
              name="hrInterviewAssessment"
              label="HR score / assessment"
              form={form}
              options={interviewAssessmentOptions}
            />
          </Col>
        </Row>
        <CandidateAttachements attachments={hrInterviewAttachements} handleAttachmentUpload={attachFile} />
        <FormTextArea rows={19} name="hrInterviewSummary" label="HR interview description" />
        {standAloneMode && (
          <StyledRow justifyContent="flex-end" marginTop="14px">
            <SubmitButton onClick={submitForm} icon={<SaveButtonIcon />}>
              Save
            </SubmitButton>
          </StyledRow>
        )}
      </StyledColumn>
    </Form>
  );
};
